import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Users,
  Megaphone,
  LogOut,
  ShieldCheck,
  Tag,
  Lock,
  Menu,
  X as CloseIcon,
  Sparkles,
  Ban,
  BarChart3,
  BookOpen,
  Inbox,
  MapPin,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { clearAdminToken } from "../../utils/adminAuth";

const SIDEBAR_KEY = "dmm_admin_sidebar_collapsed";

/**
 * Dark navy admin console shell with a persistent sidebar.
 * Matches the public site palette: bg-slate-950 base, slate-900 surfaces,
 * blue → cyan → purple accents. Mobile-friendly (collapsible drawer).
 *
 * Desktop sidebar can be COLLAPSED to an icon-only rail so the main
 * content gets more horizontal real estate. State is persisted in
 * localStorage so it survives page reloads.
 */
export default function AdminShell({ children, pageTitle, pageDescription, rightSlot }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === "1";
    } catch { return false; }
  });

  // Persist collapse preference across reloads
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, collapsed ? "1" : "0");
    } catch { /* localStorage unavailable */ }
  }, [collapsed]);

  const navItems = [
    { label: "Publishers", href: "/admin/publishers", icon: Users, desc: "Applications & KYC" },
    { label: "Blacklist", href: "/admin/blacklist", icon: Ban, desc: "Disapproved & terminated" },
    { label: "Analytics", href: "/admin/analytics", icon: BarChart3, desc: "Live visitors & traffic" },
    { label: "Campaigns", href: "/admin/campaigns", icon: Megaphone, desc: "Broadcast emails" },
    { label: "Payment Book", href: "/admin/payments", icon: BookOpen, desc: "Manual payout ledger" },
    { label: "Offer Requests", href: "/admin/offer-requests", icon: Inbox, desc: "Live inbox of applications" },
    { label: "Zip Manager", href: "/admin/zip-manager", icon: MapPin, desc: "Sync target zip codes" },
    { label: "Offers", href: "/admin/offers", icon: Tag, desc: "Public offer catalog" },
    { label: "Security", href: "/admin/security", icon: Lock, desc: "2FA & sessions" },
  ];

  const handleLogout = () => {
    clearAdminToken();
    navigate("/admin/login");
  };

  const isActive = (href) => location.pathname.startsWith(href);

  // Build the sidebar contents. `isRail` = collapsed icon-only mode.
  // Note: mobile drawer always renders in expanded mode regardless of collapsed state.
  const renderSidebar = (isRail) => (
    <>
      {/* Brand */}
      <div className={`${isRail ? "px-3 pt-4 pb-4" : "px-5 pt-5 pb-6"} border-b border-slate-800/70`}>
        <Link
          to="/admin/publishers"
          className={`flex items-center ${isRail ? "justify-center" : "gap-3"} group`}
          data-testid="admin-brand-link"
          onClick={() => setMobileOpen(false)}
          title={isRail ? "DMM Control" : undefined}
        >
          <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:shadow-blue-500/40 transition-all flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.25} />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-slate-950 animate-pulse" />
          </div>
          {!isRail && (
            <div className="leading-none">
              <div className="font-display font-bold text-[15px] text-white tracking-tight flex items-center gap-1.5">
                DMM Control
                <Sparkles className="w-3 h-3 text-cyan-400" />
              </div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mt-1">
                Admin Console
              </div>
            </div>
          )}
        </Link>
      </div>

      {/* Nav */}
      <nav className={`flex-1 ${isRail ? "px-2" : "px-3"} py-4 space-y-1 overflow-y-auto`}>
        {!isRail && (
          <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
            Workspace
          </p>
        )}
        {navItems.map((item) => {
          const active = isActive(item.href);
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setMobileOpen(false)}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              title={isRail ? `${item.label} · ${item.desc}` : undefined}
              className={`admin-sidebar-link group flex ${
                isRail ? "justify-center py-2.5" : "items-start gap-3 px-3 py-2.5"
              } rounded-lg text-[13px] font-medium border transition-all ${
                active ? "active" : "border-transparent text-slate-400"
              }`}
            >
              <div
                className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 transition-all ${
                  active
                    ? "bg-gradient-to-br from-blue-500/20 to-purple-500/20 text-blue-300"
                    : "bg-slate-800/50 text-slate-500 group-hover:text-slate-300 group-hover:bg-slate-800"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={2.25} />
              </div>
              {!isRail && (
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className={`font-semibold ${active ? "text-white" : ""}`}>{item.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">{item.desc}</div>
                </div>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className={`${isRail ? "p-2" : "p-3"} border-t border-slate-800/70`}>
        <button
          onClick={handleLogout}
          data-testid="admin-logout-btn"
          title={isRail ? "Sign out" : undefined}
          className={`w-full flex items-center ${isRail ? "justify-center" : "gap-2.5"} px-3 py-2.5 rounded-lg text-[13px] font-medium text-slate-400 hover:text-white hover:bg-red-500/10 hover:border-red-500/30 border border-transparent transition-all`}
        >
          <LogOut className="w-4 h-4 flex-shrink-0" strokeWidth={2.25} />
          {!isRail && "Sign out"}
        </button>
        {!isRail && (
          <div className="mt-3 px-3 py-2 rounded-md bg-slate-900/60 border border-slate-800/70">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-0.5">
              System
            </div>
            <div className="text-[11px] text-emerald-400 font-semibold flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              All services operational
            </div>
          </div>
        )}
      </div>
    </>
  );

  const sidebarWidth = collapsed ? "w-16" : "w-64";
  const contentPad = collapsed ? "lg:pl-16" : "lg:pl-64";

  return (
    <div className="admin-shell min-h-screen">
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex fixed inset-y-0 left-0 ${sidebarWidth} flex-col bg-slate-950/80 backdrop-blur-xl border-r border-slate-800/70 z-40 transition-[width] duration-300 ease-in-out`}
      >
        {renderSidebar(collapsed)}

        {/* Collapse toggle — floating pill straddling the sidebar edge */}
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          data-testid="admin-sidebar-collapse"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="absolute -right-3 top-20 hidden lg:flex w-6 h-6 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:text-white hover:bg-blue-600 hover:border-blue-500 items-center justify-center shadow-lg transition-all z-50"
        >
          {collapsed ? (
            <PanelLeftOpen className="w-3 h-3" strokeWidth={2.5} />
          ) : (
            <PanelLeftClose className="w-3 h-3" strokeWidth={2.5} />
          )}
        </button>
      </aside>

      {/* Mobile drawer — always expanded */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-72 flex flex-col bg-slate-950 border-r border-slate-800/70 z-50 animate-in slide-in-from-left">
            {renderSidebar(false)}
          </aside>
        </>
      )}

      {/* Main content column */}
      <div className={`${contentPad} transition-[padding] duration-300 ease-in-out`}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-slate-950/85 backdrop-blur-xl border-b border-slate-800/70">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setMobileOpen((v) => !v)}
                className="lg:hidden p-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-800/70 transition"
                data-testid="admin-sidebar-toggle"
              >
                {mobileOpen ? <CloseIcon className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              {pageTitle && (
                <div className="min-w-0">
                  <h1 className="font-display text-[17px] sm:text-[19px] font-bold tracking-tight text-white truncate">
                    {pageTitle}
                  </h1>
                  {pageDescription && (
                    <p className="hidden sm:block text-[11.5px] text-slate-400 -mt-0.5 truncate">
                      {pageDescription}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">{rightSlot}</div>
          </div>
        </header>

        {/* Content */}
        <main className="max-w-[1440px] mx-auto px-4 sm:px-6 py-6 sm:py-8">{children}</main>
      </div>
    </div>
  );
}

/* ---------- Shared primitives ---------- */

export const StatCard = ({ label, value, icon: Icon, tone = "neutral", tip, testId }) => {
  const tones = {
    neutral: {
      v: "text-white",
      i: "text-slate-400",
      accent: "from-slate-500/20 to-slate-500/0",
      ring: "ring-slate-700/50",
    },
    info: {
      v: "text-blue-300",
      i: "text-blue-400",
      accent: "from-blue-500/20 to-blue-500/0",
      ring: "ring-blue-500/30",
    },
    success: {
      v: "text-emerald-300",
      i: "text-emerald-400",
      accent: "from-emerald-500/20 to-emerald-500/0",
      ring: "ring-emerald-500/30",
    },
    warning: {
      v: "text-amber-300",
      i: "text-amber-400",
      accent: "from-amber-500/20 to-amber-500/0",
      ring: "ring-amber-500/30",
    },
    danger: {
      v: "text-red-300",
      i: "text-red-400",
      accent: "from-red-500/20 to-red-500/0",
      ring: "ring-red-500/30",
    },
    violet: {
      v: "text-violet-300",
      i: "text-violet-400",
      accent: "from-violet-500/20 to-violet-500/0",
      ring: "ring-violet-500/30",
    },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div
      title={tip || label}
      data-testid={testId}
      className={`relative overflow-hidden rounded-xl border border-slate-800/70 bg-slate-900/60 px-4 py-3.5 hover:border-slate-700 hover:bg-slate-900/80 transition-all group`}
    >
      <div
        className={`absolute inset-0 bg-gradient-to-br ${t.accent} opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none`}
      />
      <div className="relative flex items-center justify-between mb-1.5">
        <p className="text-[10px] font-bold tracking-[0.16em] uppercase text-slate-500">
          {label}
        </p>
        {Icon && (
          <div className={`w-6 h-6 rounded-md bg-slate-800/70 flex items-center justify-center ring-1 ${t.ring}`}>
            <Icon className={`w-3.5 h-3.5 ${t.i}`} strokeWidth={2.25} />
          </div>
        )}
      </div>
      <p className={`relative font-display text-2xl font-bold tabular-nums ${t.v}`}>{value}</p>
    </div>
  );
};

export const StatusPill = ({ tone, label }) => {
  const tones = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    orange: "bg-orange-500/15 text-orange-300 border-orange-500/30",
    red: "bg-red-500/15 text-red-300 border-red-500/30",
    rose: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    slate: "bg-slate-800/70 text-slate-300 border-slate-700",
    blue: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  };
  const cls = tones[tone] || tones.slate;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full bg-current opacity-70`} />
      {label}
    </span>
  );
};
